/**
 * Course deep-link routing · hash-based (`#/ticket/MS-01`) · 28 JUN 2026 (Genie).
 *
 * Enables open-in-new-tab / shareable / back-forward URLs for course screens.
 * Previously the whole app lived at `/` with screen state only (no per-page URL),
 * so middle/Ctrl-click never opened a real page.
 *
 * Hash (not path) chosen to coexist with the existing atelier (`#/atelier?...`)
 * and auth/oauth hashes, and to need zero Netlify config. `parseCourseHash`
 * returns `null` for anything it doesn't own (atelier / auth / unknown) so those
 * flows are untouched.
 *
 * Screen ↔ hash map:
 *   dashboard   → #/dashboard          sprints (map) → #/map
 *   sprint      → #/sprint/<id>        progress      → #/progress
 *   ticket      → #/ticket/<id>        profile       → #/profile
 *   minilesson  → #/lesson/<id>        practice      → #/practice
 *   review      → #/review/<sprintId>  portfolio     → #/portfolio
 *   finaltest   → #/finaltest/<sprintId>
 *   exam        → #/exam/<bankId>      mock          → #/mock/<slot>
 *
 * NOT deep-linkable (transient / onboarding): `welcome`, `peek` (depends on the
 * non-persisted peekSprintId · см. lib/lastScreen.ts RESTORABLE_SCREENS note).
 */

export type CourseScreen =
  | 'dashboard'
  | 'sprint'
  | 'ticket'
  | 'minilesson'
  | 'sprints'
  | 'progress'
  | 'profile'
  | 'practice'
  | 'portfolio'
  | 'review'
  | 'finaltest'
  | 'exam'
  | 'mock'
  // slot_payments Phase C (11 JUL 2026): shareable pricing/tariff screen.
  | 'pricing';

export interface CourseRoute {
  screen: CourseScreen;
  sprintId?: string;
  ticketId?: string;
  lessonId?: string;
  examBank?: string;
  mockSlot?: 'mock1' | 'mock2';
}

const enc = encodeURIComponent;
const dec = decodeURIComponent;

/** Build the URL hash (incl. leading `#`) for a course route. */
export function routeToHash(r: CourseRoute): string {
  switch (r.screen) {
    case 'dashboard':
      return '#/dashboard';
    case 'sprint':
      // Bare `#/sprint` (no id) is valid — opens the current/last sprint (App
      // falls back to progress.last.sprintId). Used by the «Доска» nav item, which
      // has no specific sprint to point at.
      return r.sprintId ? `#/sprint/${enc(r.sprintId)}` : '#/sprint';
    case 'ticket':
      return r.ticketId ? `#/ticket/${enc(r.ticketId)}` : '#/dashboard';
    case 'minilesson':
      return r.lessonId ? `#/lesson/${enc(r.lessonId)}` : '#/dashboard';
    case 'sprints':
      return '#/map';
    case 'progress':
      return '#/progress';
    case 'profile':
      return '#/profile';
    case 'practice':
      return '#/practice';
    case 'portfolio':
      return '#/portfolio';
    case 'review':
      return r.sprintId ? `#/review/${enc(r.sprintId)}` : '#/map';
    case 'finaltest':
      return r.sprintId ? `#/finaltest/${enc(r.sprintId)}` : '#/map';
    case 'exam':
      return `#/exam/${enc(r.examBank ?? 'block1-exam')}`;
    case 'mock':
      return `#/mock/${r.mockSlot ?? 'mock1'}`;
    case 'pricing':
      return '#/pricing';
  }
}

/**
 * Parse a URL hash into a course route, or `null` if it isn't a course hash
 * (atelier `#/atelier`, auth tokens, empty, or unknown head). Tolerates an
 * optional trailing `?query`.
 */
export function parseCourseHash(hash: string): CourseRoute | null {
  if (!hash || !hash.startsWith('#/')) return null;
  const clean = hash.slice(2).split('?')[0] ?? '';
  const sep = clean.indexOf('/');
  const head = sep < 0 ? clean : clean.slice(0, sep);
  const rest = sep < 0 ? '' : clean.slice(sep + 1);

  switch (head) {
    case 'dashboard':
      return { screen: 'dashboard' };
    case 'sprint':
      // Bare `#/sprint` → current sprint (App resolves the id from progress.last).
      return rest ? { screen: 'sprint', sprintId: dec(rest) } : { screen: 'sprint' };
    case 'ticket':
      return rest ? { screen: 'ticket', ticketId: dec(rest) } : null;
    case 'lesson':
      return rest ? { screen: 'minilesson', lessonId: dec(rest) } : null;
    case 'map':
      return { screen: 'sprints' };
    case 'progress':
      return { screen: 'progress' };
    case 'profile':
      return { screen: 'profile' };
    case 'practice':
      return { screen: 'practice' };
    case 'portfolio':
      return { screen: 'portfolio' };
    case 'review':
      return rest ? { screen: 'review', sprintId: dec(rest) } : null;
    case 'finaltest':
      return rest ? { screen: 'finaltest', sprintId: dec(rest) } : null;
    case 'exam':
      return rest ? { screen: 'exam', examBank: dec(rest) } : null;
    case 'mock':
      return rest === 'mock1' || rest === 'mock2' ? { screen: 'mock', mockSlot: rest } : null;
    case 'pricing':
      return { screen: 'pricing' };
    default:
      // #/atelier, auth/oauth hashes, anything else — not ours.
      return null;
  }
}

/**
 * Map a Sidebar / BottomTabs nav key to a course route, or `null` for keys that
 * are NOT navigable pages (overlays/widgets: glossary + atelier modals, buddy,
 * theme) — those stay plain `<button>`s. Lets the nav render real `<a href>`
 * items (open-in-new-tab) for the page sections. `sprint` → bare `#/sprint`
 * (current sprint · resolved from progress.last).
 */
export function navKeyToRoute(key: string): CourseRoute | null {
  switch (key) {
    case 'dashboard':
      return { screen: 'dashboard' };
    case 'sprints':
      return { screen: 'sprints' };
    case 'sprint':
      return { screen: 'sprint' };
    case 'practice':
      return { screen: 'practice' };
    case 'progress':
      return { screen: 'progress' };
    case 'portfolio':
      return { screen: 'portfolio' };
    case 'profile':
      return { screen: 'profile' };
    default:
      return null;
  }
}
